import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';

import { BrokenConnection, collectBrokenConnections, connectionBrokenToast } from '../utils/connectionHealth';
import { AuthService } from './auth.service';
import { DdrService } from './ddr.service';
import { MCPServer, McpService } from './mcp.service';
import { SessionStorageService } from './session-storage.service';
import { ToastService } from './toast.service';

/**
 * Shown-alert store. Keyed by user and keyed by connection.
 * Presence of a key means "we already told them about THIS break". Cleared
 * when the connection is healthy again so the next break can toast.
 */
const SHOWN_KEY = 'ciq.connectionAlert.shown';

/** A refused request (expired or missing token, or a role the route rejects),
 *  as opposed to the servers list being unavailable. */
export function isAuthRefusal(err: unknown): boolean {
    return err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403);
}

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 30_000;

type ShownStore = Record<string, string[]>;

/**
 * Watches native + MCP connections and raises a one-shot toast when one
 * breaks. The toast is dismissed by the user; localStorage remembers we
 * already showed it so a reload or the 5-minute poll does not stack.
 *
 * At most ONE toast per connection per page session, whatever the grant does
 * in between. The localStorage flag alone was cleared the moment a grant read
 * healthy, so a grant flapping between healthy and broken (an `expires_at`
 * inside the leeway, a servers request failing every other poll) re-toasted
 * on every poll: John watched "Back Office is disconnected" arrive about once
 * a minute (2026-09-15). The current broken set is exposed as a signal so the
 * sidebar can carry the state as a badge instead.
 * Eagerly injected from the app root.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionHealthService {
    private readonly auth = inject(AuthService);
    private readonly ddr = inject(DdrService);
    private readonly mcp = inject(McpService);
    private readonly toast = inject(ToastService);
    private readonly storage = inject(SessionStorageService);
    private readonly destroyRef = inject(DestroyRef);

    private started = false;
    private inFlight = false;
    private lastCheckAt = 0;
    private readonly toastedThisSession = new Set<string>();
    private readonly _broken = signal<readonly BrokenConnection[]>([]);
    /** Connections currently broken, refreshed on every check. Drives the sidebar badge. */
    readonly brokenConnections = this._broken.asReadonly();
    private intervalId: ReturnType<typeof setInterval> | null = null;

    private readonly onVisibility = (): void => {
        if (!document.hidden) {
            void this.check();
        }
    };

    // noinspection JSUnusedLocalSymbols
    private readonly _onAuth = effect(() => {
        const live = this.auth.isAuthenticated() && !this.auth.isGuest();
        if (live) {
            this.start();
        } else {
            this.stop();
        }
    });

    constructor() {
        this.destroyRef.onDestroy(() => this.stop());
    }

    /**
     * Re-read grants and toast any new breaks. `force` skips the 30s gap
     * (OAuth bounce: we just wrote a new grant and need to clear the flag).
     */
    async check(force = false): Promise<void> {
        if (!this.auth.isAuthenticated() || this.auth.isGuest()) {
            return;
        }
        if (this.inFlight) {
            return;
        }
        if (!force && this.lastCheckAt && Date.now() - this.lastCheckAt < MIN_CHECK_GAP_MS) {
            return;
        }
        this.inFlight = true;
        try {
            const [natives, grants, serversRead] = await Promise.all([
                this.ddr.listConnections().catch(() => []),
                this.mcp.listMyGrants().catch(() => []),
                this.mcp
                    .listMyServers()
                    .then(servers => ({ servers, failed: false, unauthorized: false }))
                    .catch((err: unknown) => ({
                        servers: [] as MCPServer[],
                        failed: true,
                        unauthorized: isAuthRefusal(err),
                    })),
            ]);
            if (serversRead.unauthorized) {
                // A 401 or 403 on the servers list is an auth state, not an
                // outage: the poll ran on an access token the proactive
                // refresh had not replaced yet. Judging grants without their
                // servers here is what flapped every affected user's
                // connection to "broken" for one poll (prod, 2026-09-16 to
                // 18: ~5,000 such 401s across ~1,400 clients in three days).
                // Skip this poll; lastCheckAt stays put so the next tick
                // retries at once.
                return;
            }
            const { broken, healthyKeys } = collectBrokenConnections({
                natives,
                grants,
                servers: serversRead.servers,
                serversUnavailable: serversRead.failed,
            });
            this.clearShown(healthyKeys);
            this._broken.set(broken);
            for (const item of broken) {
                if (this.hasShown(item.key) || this.toastedThisSession.has(item.key)) {
                    continue;
                }
                this.markShown(item.key);
                this.toastedThisSession.add(item.key);
                this.toast.warning(connectionBrokenToast(item.label), { duration: 0 });
            }
            this.lastCheckAt = Date.now();
        } finally {
            this.inFlight = false;
        }
    }

    private start(): void {
        if (this.started) {
            return;
        }
        this.started = true;
        void this.check(true);
        this.intervalId = setInterval(() => void this.check(), CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.onVisibility);
    }

    private stop(): void {
        this.started = false;
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    private userKey(): string | null {
        const userId = this.auth.state().user?.user_id;
        return typeof userId === 'number' ? String(userId) : null;
    }

    private readStore(): ShownStore {
        return this.storage.read<ShownStore>(SHOWN_KEY, {}, 'local');
    }

    private writeStore(store: ShownStore): void {
        this.storage.write(SHOWN_KEY, store, 'local');
    }

    private shownForUser(): string[] {
        const user = this.userKey();
        if (!user) {
            return [];
        }
        return this.readStore()[user] ?? [];
    }

    private hasShown(connectionKey: string): boolean {
        return this.shownForUser().includes(connectionKey);
    }

    private markShown(connectionKey: string): void {
        const user = this.userKey();
        if (!user) {
            return;
        }
        const store = this.readStore();
        const current = store[user] ?? [];
        if (current.includes(connectionKey)) {
            return;
        }
        this.writeStore({ ...store, [user]: [...current, connectionKey] });
    }

    private clearShown(healthyKeys: string[]): void {
        const user = this.userKey();
        if (!user || healthyKeys.length === 0) {
            return;
        }
        const store = this.readStore();
        const current = store[user] ?? [];
        if (current.length === 0) {
            return;
        }
        const drop = new Set(healthyKeys);
        const next = current.filter(key => !drop.has(key));
        if (next.length === current.length) {
            return;
        }
        this.writeStore({ ...store, [user]: next });
    }
}
